/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getSelectedCategoryPath,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography from "@insite/mobius/Typography";
import * as React from "react";
import { connect } from "react-redux";
import Skeleton from "@eisinc/App/Components/Skeleton/Skeleton";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";

// XNG-Change
export interface CategoryNameStyles {
    skeletonContainer?: InjectableCss;
}
export const categoryNameStyles: CategoryNameStyles = {};

const styles = categoryNameStyles;

// XNG-Change
const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const path =
        getSelectedCategoryPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const catalogPage = getCatalogPageStateByPath(state, path).value;
    const searchPath = getSettingsCollection(state).searchSettings.searchPath;
    return {
        isSearchResultsPage: location.pathname.toLowerCase().startsWith(`/${searchPath.toLowerCase()}`),
        isBrandPage: !!catalogPage?.brandId,
    };
};

type Props = WidgetProps & HasCategoryContext & ReturnType<typeof mapStateToProps>;

const CategoryName = ({ category, isSearchResultsPage, isBrandPage }: Props) => {
    // XNG-Change
    if (!category) {
        if (isSearchResultsPage || isBrandPage) {
            return null;
        }

        return (
            <StyledWrapper {...styles.skeletonContainer}>
                <Skeleton height="38px" />
            </StyledWrapper>
        );
    }

    // XNG-Changes: all will be h1, no need to check for search crawler
    // const isSearchCrawler = getIsWebCrawler();

    return (
        <Typography
            as="h1"
            variant="h1"
            data-test-selector={`categoryName_${category.shortDescription ?? category.name}`}
        >
            {category.shortDescription ?? category.name}{" "}
        </Typography>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withCategory(CategoryName)),
    definition: {
        group: "Category Details",
        icon: "folder-bookmark",
        allowedContexts: ["CategoryDetailsPage", "ProductDetailsPage", "ProductListPage"],
    },
};

export default widgetModule;
